import { useEffect, useRef, useState } from "react";

import { CaretDownIcon } from "@phosphor-icons/react";
import * as Sentry from "@sentry/react";
import { startCase } from "lodash";
import { useHistory, useLocation } from "react-router-dom";
import { Temporal } from "temporal-polyfill";

import {
  Button,
  classNames,
  Text,
  toastError,
  toastSuccess,
} from "@alpacahq/ui/old";

import AmplitudeProvider from "~/AmplitudeProvider";
import {
  getErrorMessage,
  useGetCurrentOwner,
  useGetIpAddress,
  useMutationRequest,
} from "~/v2/api/hooks";
import {
  getAcatsTransferDetails,
  type PostAcatsTransferRequest,
  postAcatsTransfer,
} from "~/v2/api/rest/acats";
import { AgreementBox } from "~/v2/components/form";
import { Path } from "~/v2/path";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { AcatsTransferManager } from "~/v2/utils/acatsTransferManager";
import { Layout } from "../../common/Layout";
import type { AcatsLocationState } from "../../common/types";
import { formatCash } from "../../common/util";
import { BackButton } from "../BackButton";
import { DisclaimerModal } from "./DisclaimerModal";
import { Equities } from "./Equities";
import { Line } from "./Line";
import { Row } from "./Row";
import { SuccessModal } from "./SuccessModal";

const Review = () => {
  const history = useHistory();
  const { state: locationState } = useLocation<AcatsLocationState>();

  const [isEquityDropdownOpen, setIsEquityDropdownOpen] = useState(true);
  const [isDisclaimerOpen, setIsDisclaimerOpen] = useState(false);
  const [isDisclaimerConfirmed, setIsDisclaimerConfirmed] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);

  const { currentAccount, isLoading: isAccountLoading } = useAccountContext({
    liveOnly: true,
  });

  const { currentOwner, isLoading: isCurrentOwnerLoading } = useGetCurrentOwner(
    {
      accountId: currentAccount?.id,
      enabled: !!currentAccount?.id,
    },
  );

  const { ipAddress } = useGetIpAddress();

  const browserDatetimeContextRef = useRef<Record<string, unknown> | null>(
    null,
  );

  const onBackClicked = () =>
    history.replace({
      pathname: Path.ROUTE_PORTFOLIO_TRANSFER,
      state: undefined,
    });

  const onPreviousClicked = () => {
    history.replace({
      pathname:
        locationState?.transferType === "PARTIAL"
          ? Path.ROUTE_PORTFOLIO_TRANSFER_PARTIAL_TRANSFER
          : Path.ROUTE_PORTFOLIO_TRANSFER_ASSETS_HOLDING,
      state: locationState,
    });
  };

  const createTransferMutation = useMutationRequest(
    (data: PostAcatsTransferRequest) =>
      postAcatsTransfer(currentAccount?.id ?? "", data),
    {
      onSuccess: async (response) => {
        toastSuccess("Transfer created successfully");
        AmplitudeProvider.dispatch("acats_submitted", {
          transfer_type: locationState?.transferType,
        });

        // Add the transfer to local storage for banner display
        if (currentAccount?.id && currentOwner?.owner_id) {
          try {
            // Fetch the full transfer details from the API
            const transferDetails = await getAcatsTransferDetails(
              currentAccount.id,
              response.acats_id,
            );

            // Store in AcatsTransferManager
            AcatsTransferManager.addTransfer(
              currentOwner.owner_id,
              currentAccount.id,
              transferDetails,
            );
          } catch (error) {
            console.error(
              "Failed to store ACATS transfer for banner display:",
              error,
            );
          }
        }

        setIsSuccessOpen(true);
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        const isSignedAtError = /signed.?at/i.test(errorMessage);

        if (isSignedAtError) {
          Sentry.captureException(error, {
            level: "error",
            tags: {
              component: "acats_review",
              error_type: "signed_at_future",
            },
            extra: {
              errorMessage,
              browserDatetime: browserDatetimeContextRef.current,
              requestParams: {
                accountId: currentAccount?.id,
                transferType: locationState?.transferType,
                brokerName: locationState?.brokerName,
                brokerNumber: locationState?.brokerNumber,
                ipAddress,
              },
            },
          });

          toastError(
            "Something went wrong submitting your transfer. Please try again, and if the issue persists, contact support at support@alpaca.markets.",
            { toastId: "acats-transfer-error" },
          );
        } else {
          Sentry.captureException(error, {
            level: "error",
            tags: { component: "acats_review" },
            extra: { errorMessage },
          });
          toastError(`Failed to create transfer: ${errorMessage}`, {
            toastId: "acats-transfer-error",
          });
        }
      },
    },
  );

  const onConfirmClicked = () => {
    const instant = Temporal.Now.instant();
    const signedAt = instant.toString({ fractionalSecondDigits: 0 });
    const zdt = instant.toZonedDateTimeISO(Temporal.Now.timeZoneId());

    browserDatetimeContextRef.current = {
      isoString: instant.toString(),
      signedAtSent: signedAt,
      timezoneOffset: zdt.offset,
      timezone: zdt.timeZoneId,
      locale: navigator.language,
      epochMs: instant.epochMilliseconds,
    };

    createTransferMutation.mutate({
      contra_account_details: {
        account_number: locationState?.accountNumber ?? "",
        customer_name: currentAccount?.name ?? "",
        customer_tax_id: locationState?.taxId ?? "",
        broker_number: locationState?.brokerNumber ?? "",
        broker_name: locationState?.brokerName?.substring(0, 100) ?? "",
      },
      transfer_details: {
        // @ts-expect-error: allow this to fail if there is no transfer type
        type: locationState?.transferType ?? "",
        ...(locationState?.transferType === "PARTIAL" &&
        locationState.cashBalance
          ? {
              cash: [{ amount: locationState.cashBalance, currency: "USD" }],
            }
          : {}),
        ...(locationState?.transferType === "PARTIAL" &&
        locationState.equities?.length
          ? {
              securities: {
                equities:
                  locationState.equities?.map((e) => ({
                    cusip: e.cusip,
                    position: e.position,
                    qty: e.qty,
                  })) ?? [],
              },
            }
          : {}),
      },
      ip_address: ipAddress,
      signed_at: signedAt,
    });
  };

  const onOpenDisclaimerClicked = () => setIsDisclaimerOpen(true);

  const onTransferClicked = () =>
    history.replace({
      pathname: Path.ROUTE_FUNDING_HISTORY,
      search: "?tab=portfolio-transfers",
      state: undefined,
    });

  const onHomeClicked = () =>
    history.replace({
      pathname: Path.ROUTE_DASHBOARD,
      state: undefined,
    });

  useEffect(() => {
    if (!locationState?.transferType) {
      history.replace({
        pathname: Path.ROUTE_PORTFOLIO_TRANSFER_ASSETS_HOLDING,
        state: locationState,
      });
    }
    if (!locationState?.taxId || !locationState?.accountNumber) {
      history.replace({
        pathname: Path.ROUTE_PORTFOLIO_TRANSFER_ACCOUNT_INFO,
        state: locationState,
      });
    }
    if (!locationState?.brokerName || !locationState?.brokerNumber) {
      history.replace({
        pathname: Path.ROUTE_PORTFOLIO_TRANSFER_ORIGINATING_BROKER,
        state: locationState,
      });
    }
  }, [locationState, history.replace]);

  return (
    <div>
      <BackButton
        onClick={onBackClicked}
        label="Back to Transfer Funds"
        className="mb-4"
      />
      <Layout title="Review Transfer Details" className="mt-2 pt-0">
        <div className="flex w-full max-w-md flex-col gap-5">
          <div className="flex flex-col gap-3">
            <Row label="Alpaca Account">
              <div>
                <Text>
                  {currentAccount?.account_type === "trading"
                    ? "Individual Account"
                    : startCase(currentAccount?.account_type ?? "")}
                </Text>
                <Text>{currentAccount?.account_number}</Text>
              </div>
            </Row>
            <Line />
            <Row label="Brokerage Name">
              <Text>{locationState?.brokerName}</Text>
            </Row>
            <Row label="Brokerage account number">
              <Text>{locationState?.brokerNumber}</Text>
            </Row>
            <Row label="Legal Name">
              <Text>{currentAccount?.name}</Text>
            </Row>
            {/* <Row label="Tax ID">
            <Text></Text>
          </Row> */}
            <Line />
            <Row label="Transfer type">
              <Text>
                {locationState?.transferType === "FULL"
                  ? "Full Transfer (Cash & Equity)"
                  : "Partial Transfer"}
              </Text>
            </Row>
            {locationState?.transferType === "PARTIAL" && (
              <>
                <Row label="Transfer amount">
                  <div></div>
                </Row>
                <div className="flex w-full flex-col gap-3 pl-4">
                  <Row label="Cash Balance">
                    <Text>
                      {locationState?.cashBalance
                        ? formatCash(Number(locationState?.cashBalance))
                        : "N/A"}
                    </Text>
                  </Row>
                  <Row label="Equity">
                    <button
                      type="button"
                      onClick={() => setIsEquityDropdownOpen((prev) => !prev)}
                      aria-label="Toggle equity dropdown"
                    >
                      <CaretDownIcon
                        size={20}
                        className={classNames(
                          "transition-transform duration-300",
                          !isEquityDropdownOpen ? "rotate-180" : "",
                        )}
                      />
                    </button>
                  </Row>
                  <Equities
                    isOpen={isEquityDropdownOpen}
                    equities={locationState?.equities ?? []}
                  />
                </div>
              </>
            )}
            <Row label="Processing Time">
              <Text>Est. 5 Business Days</Text>
            </Row>
          </div>
          <AgreementBox
            isAccepted={isDisclaimerConfirmed}
            label="Disclaimer"
            onOpen={onOpenDisclaimerClicked}
          />
          <DisclaimerModal
            isOpen={isDisclaimerOpen}
            onCancel={() => {
              setIsDisclaimerOpen(false);
              setIsDisclaimerConfirmed(false);
            }}
            onConfirm={() => {
              setIsDisclaimerOpen(false);
              setIsDisclaimerConfirmed(true);
            }}
          />
          <div className="flex items-center justify-between gap-4">
            <Button
              variant="v2/stroke"
              onClick={onPreviousClicked}
              type="button"
            >
              Previous
            </Button>
            <Button
              variant="v2/filled"
              onClick={onConfirmClicked}
              type="button"
              disabled={
                !isDisclaimerConfirmed ||
                isAccountLoading ||
                isCurrentOwnerLoading
              }
            >
              Confirm Transfer
            </Button>
          </div>
          <SuccessModal
            isOpen={isSuccessOpen}
            onTransferClicked={onTransferClicked}
            onHomeClicked={onHomeClicked}
          />
        </div>
      </Layout>
    </div>
  );
};

export default Review;
